import type { PlatformSettings } from "@station/contracts";
import { Alert, Button, FormField, Input, Select, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  usePlatformPermissions,
  usePlatformSettings,
  usePlatformTenant,
  useRefreshPlatform,
  useTenantOptions,
} from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { Section } from "../../features/platform/tenant-tabs.js";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { useStepUp } from "../../lib/step-up.js";

export const Route = createFileRoute("/platform/ayarlar")({ component: SettingsRoute });

/**
 * Platform ayarları (Prompt 24 web): kendi faturalarımızın kesildiği kiracı / şirket / KDV kodu, gecikme hatırlatma
 * günleri ve salt okunura düşme süresi, kapanıştan sonra saklama süresi. Değerler koda gömülü değildir (AGENTS.md §3.12).
 */
function SettingsRoute(): ReactElement {
  const { t } = useTranslation();
  const settings = usePlatformSettings();
  return (
    <PlatformPage
      title={t("platform.settings.title")}
      description={t("platform.settings.description")}
      permission="platform.catalog.read"
    >
      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : settings.isPending ? (
        <SkeletonRows rows={6} />
      ) : (
        <SettingsForm key={JSON.stringify(settings.data)} settings={settings.data} />
      )}
    </PlatformPage>
  );
}

function SettingsForm({ settings }: { settings: PlatformSettings }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPlatform();
  const withStepUp = useStepUp();
  const permissions = usePlatformPermissions();
  const canManage = permissions.has("platform.settings.manage");
  const tenants = useTenantOptions(canManage);
  const [tenantId, setTenantId] = useState(settings.billing.tenant?.id ?? "");
  const [companyId, setCompanyId] = useState(settings.billing.company?.id ?? "");
  const [vatRateCode, setVatRateCode] = useState(settings.billing.vatRateCode ?? "VAT_STANDARD");
  const [reminderDays, setReminderDays] = useState(settings.dunning.reminderDays.join(", "));
  const [graceDays, setGraceDays] = useState(String(settings.dunning.graceDays));
  const [retentionYears, setRetentionYears] = useState(String(settings.retention.years));
  const [error, setError] = useState<string | null>(null);
  const tenant = usePlatformTenant(
    tenantId || "00000000-0000-7000-8000-000000000000",
    canManage && tenantId !== "",
  );

  const save = useMutation({
    mutationFn: (body: Parameters<typeof saveSettings>[0]) => withStepUp(() => saveSettings(body)),
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("platform.settings.saved") });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const submit = (part: "billing" | "dunning" | "retention") => {
    setError(null);
    if (part === "billing") {
      if (!tenantId || !companyId || !vatRateCode.trim()) return setError(t("platform.requiredFields"));
      return save.mutate({ billing: { tenantId, companyId, vatRateCode: vatRateCode.trim() } });
    }
    if (part === "dunning") {
      const days = reminderDays
        .split(/[,\s]+/)
        .filter(Boolean)
        .map(Number);
      const grace = Number(graceDays);
      if (
        days.some((day) => !Number.isInteger(day) || day < 1 || day > 60) ||
        !Number.isInteger(grace) ||
        grace < 1 ||
        grace > 120
      )
        return setError(t("platform.settings.dunningInvalid"));
      return save.mutate({ dunning: { reminderDays: days, graceDays: grace } });
    }
    const years = Number(retentionYears);
    if (!Number.isInteger(years) || years < 1 || years > 30)
      return setError(t("platform.settings.retentionInvalid"));
    return save.mutate({ retention: { years } });
  };

  return (
    <div className="grid gap-4">
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <Section
        title={t("platform.settings.billing")}
        actions={
          settings.billing.configured ? (
            <StatusBadge tone="success">{t("platform.settings.configured")}</StatusBadge>
          ) : (
            <StatusBadge tone="warning">{t("platform.settings.notConfigured")}</StatusBadge>
          )
        }
      >
        <p className="text-sm text-muted-foreground">{t("platform.settings.billingDescription")}</p>
        {canManage ? (
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              submit("billing");
            }}
          >
            <div className="grid items-start gap-4 sm:grid-cols-3">
              <FormField label={t("platform.fields.billingTenant")} required>
                {(control) => (
                  <Select
                    {...control}
                    value={tenantId}
                    onChange={(event) => {
                      setTenantId(event.target.value);
                      setCompanyId("");
                    }}
                  >
                    <option value="">{t("platform.choose")}</option>
                    {(tenants.data ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("platform.fields.billingCompany")} required>
                {(control) => (
                  <Select
                    {...control}
                    value={companyId}
                    disabled={!tenantId}
                    onChange={(event) => setCompanyId(event.target.value)}
                  >
                    <option value="">{t("platform.choose")}</option>
                    {(tenant.data?.companies ?? []).map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.legalName}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label={t("platform.fields.vatRateCode")} required>
                {(control) => (
                  <Input
                    {...control}
                    className="font-mono"
                    value={vatRateCode}
                    onChange={(event) => setVatRateCode(event.target.value)}
                  />
                )}
              </FormField>
            </div>
            <div>
              <Button type="submit" loading={save.isPending && save.variables?.billing !== undefined}>
                {t("common.save")}
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-sm">
            {settings.billing.tenant?.name ?? "—"} · {settings.billing.company?.name ?? "—"} ·{" "}
            {settings.billing.vatRateCode ?? "—"}
          </p>
        )}
      </Section>

      <Section title={t("platform.settings.dunning")}>
        <p className="text-sm text-muted-foreground">{t("platform.settings.dunningDescription")}</p>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit("dunning");
          }}
        >
          <div className="grid items-start gap-4 sm:grid-cols-3">
            <FormField
              label={t("platform.fields.reminderDays")}
              description={t("platform.settings.reminderDaysHint")}
            >
              {(control) => (
                <Input
                  {...control}
                  disabled={!canManage}
                  value={reminderDays}
                  onChange={(event) => setReminderDays(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("platform.fields.graceDays")}>
              {(control) => (
                <Input
                  {...control}
                  disabled={!canManage}
                  inputMode="numeric"
                  className="tabular"
                  value={graceDays}
                  onChange={(event) => setGraceDays(event.target.value)}
                />
              )}
            </FormField>
          </div>
          {canManage ? (
            <div>
              <Button type="submit" loading={save.isPending && save.variables?.dunning !== undefined}>
                {t("common.save")}
              </Button>
            </div>
          ) : null}
        </form>
      </Section>

      <Section title={t("platform.settings.retention")}>
        <p className="text-sm text-muted-foreground">{t("platform.settings.retentionDescription")}</p>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit("retention");
          }}
        >
          <div className="grid items-start gap-4 sm:grid-cols-3">
            <FormField label={t("platform.fields.retentionYears")}>
              {(control) => (
                <Input
                  {...control}
                  disabled={!canManage}
                  inputMode="numeric"
                  className="tabular"
                  value={retentionYears}
                  onChange={(event) => setRetentionYears(event.target.value)}
                />
              )}
            </FormField>
          </div>
          {canManage ? (
            <div>
              <Button type="submit" loading={save.isPending && save.variables?.retention !== undefined}>
                {t("common.save")}
              </Button>
            </div>
          ) : null}
        </form>
      </Section>
    </div>
  );
}

async function saveSettings(body: {
  billing?: { tenantId: string; companyId: string; vatRateCode: string };
  dunning?: { reminderDays: number[]; graceDays: number };
  retention?: { years: number };
}) {
  return (await api.PUT("/platform/settings", { body })).data!;
}
